import React, { PureComponent } from 'react'
import client from 'lib/graphql'
import { toaster } from 'evergreen-ui'
import { get } from 'lodash'

import RecoveryCodeStep from './components/RecoveryCodeStep'
import GetAppStep from './components/Totp/GetAppStep'
import ScanCodeStep from './components/Totp/ScanCodeStep'
import { MfaOptionTypes } from './MfaApp'

import { SETUP_TOTP_MUTATION, VERIFY_TOTP_QUERY } from './graphql/totp'

enum ToptStep {
  getApp = 'getApp',
  scanCode = 'scanCode',
  recoveryCode = 'recoveryCode'
}

interface ConfigureTotpAppProps {
  configureTfaMethod: (option: MfaOptionTypes) => void
  refetchMfaStatus: any
}

interface ConfigureTotpAppState {
  step: ToptStep
  recoveryCode: string
  qrCode: string
  secret: string
  totpToken: string
}

class ConfigureTotpApp extends PureComponent<ConfigureTotpAppProps, ConfigureTotpAppState> {
  state = {
    step: ToptStep.getApp,
    recoveryCode: '',
    qrCode: '',
    secret: '',
    totpToken: ''
  }

  // QUERIES
  onSetupTotp = async () => {
    return client.mutate({
      mutation: SETUP_TOTP_MUTATION
    })
  }

  onVerifyTotp = async () => {
    return client.query({
      query: VERIFY_TOTP_QUERY,
      variables: {
        input: {
          token: this.state.totpToken
        }
      }
    })
  }

  // FUNCTIONS
  onClickBackFromGetApp = () => {
    this.props.configureTfaMethod(MfaOptionTypes.none)
  }

  onClickBackFromScan = () => {
    this.setState({
      step: ToptStep.getApp,
      recoveryCode: '',
      qrCode: '',
      secret: '',
      totpToken: ''
    })
  }

  onClickNext = async () => {
    const message = 'Something went wrong trying to enable your Authenticator App Multi-Factor Authentication'
    try {
      const setupRes = await this.onSetupTotp()
      if (get(setupRes, 'data.setupTotp.errors')) {
        return toaster.danger(message)
      }
      this.setState({
        step: ToptStep.scanCode,
        recoveryCode: get(setupRes, 'data.setupTotp.recoveryCode'),
        qrCode: get(setupRes, 'data.setupTotp.qrCode'),
        secret: get(setupRes, 'data.setupTotp.secret')
      })
    } catch (e) {
      return toaster.danger(message)
    }
  }

  onClickEnable = async () => {
    const message = 'Something went wrong trying to enable your Authenticator App Multi-Factor Authentication'
    try {
      const verifyRes = await this.onVerifyTotp()
      if (get(verifyRes, 'data.verifyTotp.errors')) {
        return toaster.danger(message)
      }
      this.setState({ step: ToptStep.recoveryCode })
      toaster.success('You have successfully enabled Authenticator App Multi-Factor Authentication')
      this.props.refetchMfaStatus()
    } catch (e) {
      return toaster.danger(message)
    }
  }

  onClickRecoveryDone = () => {
    this.setState({
      step: ToptStep.getApp,
      recoveryCode: '',
      qrCode: '',
      secret: '',
      totpToken: ''
    })

    this.props.configureTfaMethod(MfaOptionTypes.none)
  }

  onTotpTokenInputChange = (value: string) => {
    this.setState({
      totpToken: value
    })
  }

  render() {
    const { step, recoveryCode, qrCode, secret } = this.state

    if (step === ToptStep.getApp) {
      return <GetAppStep onClickNext={this.onClickNext} onClickBack={this.onClickBackFromGetApp} />
    } else if (step === ToptStep.scanCode) {
      return (
        <ScanCodeStep
          onTotpTokenInputChange={this.onTotpTokenInputChange}
          onClickEnable={this.onClickEnable}
          onClickBack={this.onClickBackFromScan}
          qrCode={qrCode}
          secret={secret}
        />
      )
    } else if (step === ToptStep.recoveryCode) {
      return <RecoveryCodeStep onClickNext={this.onClickRecoveryDone} recoveryCode={recoveryCode} />
    } else {
      return null
    }
  }
}

export default ConfigureTotpApp
